import React, { useEffect, useRef } from 'react';
import { HERO_CONTENT } from '../data/mockData';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const HeroSection = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const drawLine = (offset, amplitude, color, lineWidth) => {
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      for (let x = 0; x < canvas.width; x++) {
        const y =
          canvas.height / 2 +
          Math.sin((x + time * 0.5 + offset) * 0.003) * amplitude +
          Math.sin((x + time * 0.3 + offset * 2) * 0.007) * (amplitude * 0.5);
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Subtle glow effect
      const gradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        canvas.width * 0.4
      );
      gradient.addColorStop(0, 'rgba(131, 191, 66, 0.06)');
      gradient.addColorStop(1, 'rgba(131, 191, 66, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw flowing lines
      drawLine(0, 150, 'rgba(131, 191, 66, 0.15)', 1.5);
      drawLine(200, 120, 'rgba(131, 191, 66, 0.1)', 1);
      drawLine(400, 180, 'rgba(168, 217, 108, 0.08)', 1);
      drawLine(600, 100, 'rgba(131, 191, 66, 0.12)', 0.8);
      drawLine(800, 160, 'rgba(168, 217, 108, 0.06)', 1.2);

      time += 1;
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center bg-[#242326] overflow-hidden pt-20">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />

      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-[#83BF42] text-sm md:text-base uppercase tracking-[0.2em] mb-8 font-['Inter'] font-medium"
        >
          {HERO_CONTENT.subline}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="text-white text-4xl md:text-6xl lg:text-[80px] font-light leading-[1.1] tracking-tight font-['Inter'] mb-12"
        >
          {HERO_CONTENT.headline}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <button
            onClick={scrollToContact}
            data-testid="hero-cta-get-started"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/30 text-white text-base font-light hover:bg-white hover:text-[#242326] transition-all duration-300 font-['Inter'] group"
          >
            {HERO_CONTENT.cta.label}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FDFEFA] to-transparent" />
    </section>
  );
};

export default HeroSection;
